import {
  PAYMENTS_OPS_DEFAULT_PAGE_SIZE,
  PAYMENTS_OPS_PAGE_SIZE_OPTIONS,
} from 'src/constants/payments-ops/constants'
import { FeeWithdrawalStatus, FeeWithdrawalType } from 'src/constants/payments-ops/enums'
import {
  feeWithdrawalStatusLabels,
  feeWithdrawalTypeLabels,
} from 'src/constants/payments-ops/labels'
import type {
  FeeWithdrawal,
  FeeWithdrawalFilters,
  FetchFeeWithdrawalsPayload,
} from 'src/interfaces/feeWithdrawal'
import { formatDateOnly, formatDateTime, parseIsoDate, parseUnixTimestamp } from 'src/utils/grid'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { isUuid } from 'src/utils/partnerDriver/isUuid'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import type {
  FeeWithdrawalDetailRow,
  FeeWithdrawalsPaginationModel,
  FeeWithdrawalsUrlState,
} from './feeWithdrawals.types'

export const FEE_WITHDRAWAL_REPORT_NOT_FOUND_MESSAGE = 'No report stored for this withdrawal'

const EMPTY_VALUE = '—'
const SHORTENED_HEAD_LENGTH = 6
const SHORTENED_TAIL_LENGTH = 4

const STATUSES = Object.values(FeeWithdrawalStatus) as string[]
const TYPES = Object.values(FeeWithdrawalType) as string[]

const parseNonNegativeInteger = (value: string | null): number => {
  const parsed = Number(value)

  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0
}

/**
 * The page's URL state. The list is validated `forbidNonWhitelisted` with enum/UUID checks, so a
 * stale or hand-edited value would 400 every load — anything unknown is dropped instead.
 */
export const parseFeeWithdrawalsUrlState = (
  searchParams: URLSearchParams,
): FeeWithdrawalsUrlState => {
  const status = searchParams.get('status') ?? ''
  const type = searchParams.get('type') ?? ''
  const partnerId = searchParams.get('partnerId') ?? undefined
  const limit = parseNonNegativeInteger(searchParams.get('limit'))
  const pageSize = PAYMENTS_OPS_PAGE_SIZE_OPTIONS.includes(limit)
    ? limit
    : PAYMENTS_OPS_DEFAULT_PAGE_SIZE
  const offset = parseNonNegativeInteger(searchParams.get('offset'))

  return {
    filters: {
      status: STATUSES.includes(status) ? (status as FeeWithdrawalStatus) : null,
      type: TYPES.includes(type) ? (type as FeeWithdrawalType) : null,
      partnerId: isUuid(partnerId) ? partnerId : null,
    },
    hasReportOnly: searchParams.get('hasReport') === 'true',
    paginationModel: { page: Math.floor(offset / pageSize), pageSize },
  }
}

/** URL params for a grid page change; a new page size goes back to the first page. */
export const buildFeeWithdrawalsPaginationParams = (
  current: FeeWithdrawalsPaginationModel,
  next: FeeWithdrawalsPaginationModel,
): Record<'limit' | 'offset', string | null> => {
  if (next.pageSize !== current.pageSize) {
    return { limit: String(next.pageSize), offset: null }
  }

  return { limit: String(next.pageSize), offset: String(next.page * next.pageSize) }
}

/** Empty filters are left out — the backend treats a missing key as "any". */
export const buildFeeWithdrawalsQuery = (
  payload: FetchFeeWithdrawalsPayload,
): Record<string, string | number> => {
  const filters: Array<[keyof FeeWithdrawalFilters, string | null]> = [
    ['status', payload.status],
    ['type', payload.type],
    ['partnerId', payload.partnerId],
  ]
  const query: Record<string, string | number> = {}

  filters.forEach(([key, value]) => {
    if (value !== null && value !== '') {
      query[key] = value
    }
  })

  return { ...query, limit: payload.limit, offset: payload.offset }
}

/** "Has report" narrows the loaded page only — the API has no such filter. */
export const filterFeeWithdrawalsByReport = (
  rows: FeeWithdrawal[],
  hasReportOnly: boolean,
): FeeWithdrawal[] => {
  if (!hasReportOnly) {
    return rows
  }

  return rows.filter((row) => {
    return row.hasReport
  })
}

export const buildFeeWithdrawalReportFileName = (withdrawal: FeeWithdrawal): string => {
  return `fees-withdraw-${withdrawal.shortId || withdrawal.id}.pdf`
}

/** `txn_0123456789abcdef` → `txn_01…cdef`; short values are kept whole. */
export const shortenIdentifier = (value: string): string => {
  if (value.length <= SHORTENED_HEAD_LENGTH + SHORTENED_TAIL_LENGTH + 1) {
    return value
  }

  return `${value.slice(0, SHORTENED_HEAD_LENGTH)}…${value.slice(-SHORTENED_TAIL_LENGTH)}`
}

/** The report endpoint answers 404 when the withdrawal has no stored report. */
export const isFeeWithdrawalReportNotFound = (error: unknown): boolean => {
  return (error as { status?: number } | null)?.status === 404
}

export const resolveFeeWithdrawalReportError = (error: unknown): string => {
  if (isFeeWithdrawalReportNotFound(error)) {
    return FEE_WITHDRAWAL_REPORT_NOT_FOUND_MESSAGE
  }

  return resolveErrorMessage(error, 'Failed to load the report')
}

const orDash = (value: string | null | undefined): string => {
  return value === null || value === undefined || value === '' ? EMPTY_VALUE : value
}

/** Every withdrawal field, in reading order, for the details modal. */
export const buildFeeWithdrawalDetailRows = (
  withdrawal: FeeWithdrawal,
): FeeWithdrawalDetailRow[] => {
  return [
    { label: 'Short ID', value: orDash(withdrawal.shortId) },
    { label: 'Partner', value: orDash(withdrawal.partnerName) },
    { label: 'Partner ID', value: orDash(withdrawal.partnerId) },
    {
      label: 'Type',
      value: feeWithdrawalTypeLabels[withdrawal.type] ?? orDash(withdrawal.type),
    },
    { label: 'Amount', value: formatCentsAsUsd(withdrawal.totalAmount) },
    {
      label: 'Status',
      value: feeWithdrawalStatusLabels[withdrawal.status]?.label ?? orDash(withdrawal.status),
    },
    { label: 'Failure reason type', value: orDash(withdrawal.failureReasonType) },
    { label: 'Effective date', value: formatDateOnly(parseIsoDate(withdrawal.effectiveDate)) },
    { label: 'External transaction ID', value: orDash(withdrawal.externalTransactionId) },
    { label: 'External transaction status', value: orDash(withdrawal.externalTransactionStatus) },
    { label: 'Report', value: withdrawal.hasReport ? 'Stored' : 'No report' },
    { label: 'Created at', value: formatDateTime(parseUnixTimestamp(withdrawal.createdAt)) },
    { label: 'Updated at', value: formatDateTime(parseUnixTimestamp(withdrawal.updatedAt)) },
  ]
}
